import io from 'socket.io-client';

import httpClient from 'Utils/httpClient';
import { isLocal, isTestEnv } from 'Utils/utils';

import { currentSite, getPricingGroup, isCommunity, utmParams } from './utils';

export const getSignupServiceUrl = () => {
	if (isLocal()) {
		return '/signup';
	}
	if (isTestEnv()) {
		return 'https://signup-test.totango.com';
	}
	return `https://signup-${currentSite}.totango.com`;
};

const { searchParams } = new URL(window.location.href);

const skipPool = searchParams.get('skipPool') !== null;
const forcePool = searchParams.get('forcePool') !== null;
const featureFlags = searchParams.get('featureFlags') || undefined;
const gclid = searchParams.get('gclid');
const serviceSize = searchParams.get('serviceSize');
const clusterName = searchParams.get('clusterName');
const EXTENDED_TIMEOUT = 10 * 1000;

const signupServiceHttpClient = httpClient.create({
	baseURL: getSignupServiceUrl(),
});

export const generateAuthenticationCode = async body => {
	const { data } = await signupServiceHttpClient.post('/code', {
		skipPool,
		forcePool,
		featureFlags,
		...(serviceSize && { serviceSize }),
		...(clusterName && { clusterName }),
		...(isCommunity && {
			pricingGroup: getPricingGroup(),
		}),
		...body,
	});
	return data;
};

export const trackContact = async body => {
	const { data } = await signupServiceHttpClient.post('/track', {
		...body,
		utm_source: utmParams.utmSource,
		utm_campaign: utmParams.utmCampaign,
		utm_medium: utmParams.utmMedium,
		utm_term: utmParams.utmTerm,
		utm_content: utmParams.utmContent,
		original_path: utmParams.originalPath,
		...(gclid && { GCLID: gclid }),
		package: isCommunity ? 'Community Edition' : 'Trial',
	});
	return data;
};

export const createAccount = async body => {
	const { data } = await signupServiceHttpClient.post('/', {
		...body,
		skipPool,
		forcePool,
		featureFlags,
		...(serviceSize && { serviceSize }),
		...(clusterName && { clusterName }),
		...(isCommunity && {
			pricingGroup: getPricingGroup(),
		}),
	});
	return data;
};

export const validateSubDomain = async subdomain => {
	const { data } = await signupServiceHttpClient.post('/subdomain', {
		subdomain: subdomain.toLowerCase(),
	});
	return data;
};

export const validateUserAvailability = async (username, token) => {
	const { data } = await signupServiceHttpClient.post('/availability/user', { username, token });
	return data;
};

export const validateHumanUserAvailability = async (username, token) => {
	const { data } = await signupServiceHttpClient.post('/availability/validated/user', {
		username,
		token,
	});
	return data;
};

export const checkSignup880FeatureEnabled = async featureName => {
	const { data } = await signupServiceHttpClient.post(
		'/feature/880/enabled',
		{ featureName },
		{ timeout: EXTENDED_TIMEOUT },
	);
	return data;
};

export const decryptEmail = async ee => {
	const { data } = await signupServiceHttpClient.post('/email/decrypt', { ee });
	return data;
};

export const waitForProgress = ({ socketUrl }, username, code, callback) => {
	const socket = io(socketUrl);
	socket.on('connect', () => {
		socket.emit('room', `${username}:${code}`);
		socket.on('signup-status', data => {
			callback(data.message);
		});
	});
};

export const ERROR_TYPES = {
	AUTHENTICATION: 'AUTHENTICATION',
	DOMAIN: 'DOMAIN',
	USERNAME_TAKEN: 'USERNAME_TAKEN',
	DOMAIN_TAKEN: 'DOMAIN_TAKEN',
};

export const ERROR_TYPE_MAP = {
	MISSING_AUTHENTICATION_CODE: ERROR_TYPES.AUTHENTICATION,
	AUTHENTICATION_CODE_EXPIRED: ERROR_TYPES.AUTHENTICATION,
	AUTHENTICATION_CODE_WRONG: ERROR_TYPES.AUTHENTICATION,
	DOMAIN_NAME_INVALID: ERROR_TYPES.DOMAIN,
	USERNAME_TAKEN: ERROR_TYPES.USERNAME_TAKEN,
	DUPLICATE_SERVICE_NAME: ERROR_TYPES.DOMAIN_TAKEN,
};
